import { jsPDF } from 'jspdf';
import axios from 'axios';
import { DOCUMENTS_BY_SLUG, DOCUMENTS, KIT_LEGAL_DISCLAIMER } from '../content/executorKit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const trackDownload = (slug) => {
  // Fire-and-forget — never block the download on network state
  axios.post(`${API}/executor-kit/track-download`, { slug }).catch(() => { /* noop */ });
};

const MARGIN = 56;
const LINE_HEIGHT = 15;
const PAGE_W = 612;
const PAGE_H = 792;
const MAX_W = PAGE_W - MARGIN * 2;

// Simple **bold** parser — emits [{text, bold}] runs
const parseBold = (text) => {
  const runs = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) runs.push({ text: text.slice(last, m.index), bold: false });
    runs.push({ text: m[1], bold: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) runs.push({ text: text.slice(last), bold: false });
  return runs;
};

const addFooter = (doc, page, total) => {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text('EstateProFiler.com | The Executor Success Kit', MARGIN, PAGE_H - 30);
  doc.text(`Page ${page} of ${total}`, PAGE_W - MARGIN, PAGE_H - 30, { align: 'right' });
  doc.setTextColor(0);
};

const drawWrappedRuns = (doc, runs, x, y, maxWidth) => {
  const words = [];
  runs.forEach((run) => run.text.split(/(\s+)/).forEach((w) => words.push({ text: w, bold: run.bold })));
  let line = [];
  let lineWidth = 0;
  const flush = () => {
    let cursor = x;
    line.forEach((w) => {
      doc.setFont('helvetica', w.bold ? 'bold' : 'normal');
      doc.text(w.text, cursor, y);
      cursor += doc.getTextWidth(w.text);
    });
    y += LINE_HEIGHT;
    line = [];
    lineWidth = 0;
  };
  words.forEach((w) => {
    doc.setFont('helvetica', w.bold ? 'bold' : 'normal');
    const wWidth = doc.getTextWidth(w.text);
    if (lineWidth + wWidth > maxWidth && line.length) flush();
    line.push(w);
    lineWidth += wWidth;
  });
  if (line.length) flush();
  return y;
};

const ensureSpace = (doc, y, needed = 40) => {
  if (y + needed > PAGE_H - MARGIN) {
    doc.addPage();
    return MARGIN;
  }
  return y;
};

const renderDocument = (doc, docData, startPage = 1) => {
  if (startPage > 1) doc.addPage();
  let y = MARGIN;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(15, 61, 46);
  const titleLines = doc.splitTextToSize(docData.title, MAX_W);
  titleLines.forEach((l) => { doc.text(l, MARGIN, y); y += 24; });

  // Subtitle
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(12);
  doc.setTextColor(80);
  const subLines = doc.splitTextToSize(docData.subtitle, MAX_W);
  subLines.forEach((l) => { doc.text(l, MARGIN, y); y += 16; });
  y += 8;

  // Legal disclaimer box
  doc.setDrawColor(200);
  doc.setFillColor(246, 247, 243);
  doc.rect(MARGIN, y, MAX_W, 60, 'FD');
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(90);
  const disc = doc.splitTextToSize(KIT_LEGAL_DISCLAIMER, MAX_W - 16);
  let dy = y + 12;
  disc.forEach((l) => { doc.text(l, MARGIN + 8, dy); dy += 10; });
  y += 72;
  doc.setTextColor(0);

  // Intro
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  docData.intro.split(/\n\n/).forEach((para) => {
    y = ensureSpace(doc, y, 30);
    y = drawWrappedRuns(doc, parseBold(para), MARGIN, y, MAX_W);
    y += 6;
  });
  y += 8;

  // Sections
  docData.sections.forEach((sec) => {
    y = ensureSpace(doc, y, 60);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(15, 61, 46);
    doc.text(sec.heading, MARGIN, y);
    y += 20;
    doc.setTextColor(0);
    doc.setFontSize(11);
    sec.paragraphs.forEach((p) => {
      y = ensureSpace(doc, y, 30);
      y = drawWrappedRuns(doc, parseBold(p), MARGIN, y, MAX_W);
      y += 8;
    });
    y += 8;
  });

  // Closing footer
  y = ensureSpace(doc, y, 40);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(120);
  const closing = 'The Executor Success Kit is a free resource from EstateProFiler — software designed to help executors stay organized throughout the estate administration process. Learn more at EstateProFiler.com.';
  drawWrappedRuns(doc, [{ text: closing, bold: false }], MARGIN, y, MAX_W);
  doc.setTextColor(0);
};

export const generateSingleDocPdf = (slug) => {
  const docData = DOCUMENTS_BY_SLUG[slug];
  if (!docData) return null;
  trackDownload(slug);
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
  renderDocument(pdf, docData);
  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    addFooter(pdf, i, total);
  }
  pdf.save(`ExecutorSuccessKit_${docData.slug}.pdf`);
};

export const generateFullKitPdf = () => {
  trackDownload('full-kit');
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
  // Cover
  pdf.setFillColor(15, 61, 46);
  pdf.rect(0, 0, PAGE_W, PAGE_H, 'F');
  pdf.setTextColor(255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(32);
  pdf.text('The Executor', PAGE_W / 2, 280, { align: 'center' });
  pdf.text('Success Kit', PAGE_W / 2, 320, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(14);
  pdf.text('A practical guide for the first year of estate administration', PAGE_W / 2, 360, { align: 'center' });
  pdf.setFontSize(11);
  pdf.text('From the team at EstateProFiler', PAGE_W / 2, 480, { align: 'center' });
  pdf.text('EstateProFiler.com', PAGE_W / 2, 500, { align: 'center' });
  pdf.setTextColor(0);

  // Each document
  DOCUMENTS.forEach((docData, idx) => {
    renderDocument(pdf, docData, idx + 2);
  });

  // Numbered footers
  const total = pdf.internal.getNumberOfPages();
  for (let i = 2; i <= total; i++) {
    pdf.setPage(i);
    addFooter(pdf, i - 1, total - 1);
  }
  pdf.save('The_Executor_Success_Kit.pdf');
};
